/**
 * Grouped, editable "Review Summary" of an NCCN evaluation, shown once the
 * reviewer reaches the end of the step-by-step pager. This is the final view —
 * the reviewer keeps overriding and editing here.
 *
 * Guidelines are grouped by their effective verdict — the human reviewer's
 * verdict when present, otherwise the AI verdict — so an AI-Undetermined (or
 * overridden) guideline lands under Met or Not Met based on what the reviewer
 * decided. Met guidelines are listed collapsed under two headings — the ones
 * the reviewer marked Met, and the AI-Met ones that were never reviewed —
 * while Not-Met guidelines are expanded to the full guideline text. Each row
 * keeps the AI
 * verdict tag ("AI: Met" / "AI: Undetermined" / "AI: Not Met") alongside the
 * reviewer's "Human: …" verdict whenever one is recorded, so the AI and human
 * verdicts always stay visible and distinguishable. Each not-yet-reviewed
 * AI-Not-Met guideline gets an "Override to Met" action and its text block is
 * labelled for review.
 * @module GuidelineSummaryList
 */
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useMemo, useState } from "react";
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import { HumanVerdict } from "../../../data-model/nccnGuidelines";
import ChevronDown from "../../../svg/chevron-down";
import EditPencilIcon from "../../../svg/editPencil";
import { PlusIcon } from "../../../svg/plus-icon";
import { isBothVerdictsMet, isHumanMet } from "../utils/bothVerdictsMet";
import {
  getGuidelineDisplayPositions,
  isAiMet,
  isAiNotMet,
  isEffectiveMet,
} from "../utils/nccnReviewFeedback";
import { VERDICT_COLORS } from "../utils/verdictColors";
import { GuidelineCopyButton } from "./GuidelineCopyButton";

/** A guideline paired with its position in the full (unfiltered) list. */
interface GuidelineEntry {
  guideline: NCCNEvaluationRecord;
  index: number;
}

/** Props for {@link GuidelineSummaryList}. */
interface GuidelineSummaryListProps {
  /** All guidelines in the evaluation, in their original order. */
  guidelines: NCCNEvaluationRecord[];
  /** Opens an unreviewed AI-Not-Met guideline via "Override to Met". */
  onOverrideToMet?: (index: number) => void;
  /** Reopens a guideline via the edit pencil (Met row or reviewed Not-Met). */
  onEditReview?: (index: number) => void;
  /** Fired when a guideline's text is copied from a Met row. */
  onCopy?: (guideline: NCCNEvaluationRecord, displayIndex: number) => void;
  showHeader?: boolean;
}

const MET_COLORS = VERDICT_COLORS.met;
const NOT_MET_COLORS = VERDICT_COLORS.notMet;
const UNDETERMINED_COLORS = VERDICT_COLORS.undetermined;

/** Zero-pads a 1-based position to two digits (e.g. `3` -> `"03"`). */
const pad = (value: number): string => String(value).padStart(2, "0");

/** A label and colour pair describing a guideline's AI verdict tag. */
interface AiVerdictTagDescriptor {
  label: string;
  background: string;
  text: string;
}

/**
 * Builds the "AI: …" verdict tag descriptor for a guideline, colour-coded by
 * its AI result (Met=green, Undetermined=orange, Not Met=red).
 *
 * @param guideline - The guideline record to describe
 * @returns The tag label and its background/text colours
 */
const aiVerdictTag = (
  guideline: NCCNEvaluationRecord,
): AiVerdictTagDescriptor => {
  if (isAiMet(guideline)) {
    return {
      label: "AI: Met",
      background: MET_COLORS.background,
      text: MET_COLORS.text,
    };
  }
  if (isAiNotMet(guideline)) {
    return {
      label: "AI: Not Met",
      background: NOT_MET_COLORS.background,
      text: NOT_MET_COLORS.text,
    };
  }
  return {
    label: "AI: Undetermined",
    background: UNDETERMINED_COLORS.background,
    text: UNDETERMINED_COLORS.text,
  };
};

/**
 * Small tag reflecting the human reviewer's verdict on a guideline.
 *
 * @param props.verdict - The stored human verdict
 * @returns The rendered verdict tag, or null when there is no verdict
 */
const HumanVerdictTag: React.FC<{ verdict?: HumanVerdict | null }> = ({
  verdict,
}) => {
  if (!verdict) return null;
  const isMet = verdict === HumanVerdict.Met;
  return (
    <Tags
      label={isMet ? "Human: Met" : "Human: Not Met"}
      backgroundColor={
        isMet ? MET_COLORS.background : NOT_MET_COLORS.background
      }
      textColor={isMet ? MET_COLORS.text : NOT_MET_COLORS.text}
    />
  );
};

/**
 * Row for a guideline whose effective verdict is "Met".
 *
 * The AI verdict tag reflects the guideline's own AI result ("AI: Met" /
 * "AI: Undetermined" / "AI: Not Met"); when the reviewer recorded a verdict it
 * also carries the reviewer's "Human: …" tag (e.g. "AI: Undetermined" +
 * "Human: Met" for an undetermined guideline the reviewer passed). The row
 * shows a truncated preview of the guideline by default and expands to the full
 * guideline text when the trailing chevron is clicked. When both the AI and
 * human verdicts are Met, the expanded text carries a copy control so the
 * guideline text can be pasted into the BO Notes.
 *
 * @param props.entry - Guideline plus its original index
 * @param props.position - 1-based review-order position (for the `NN/NN` badge)
 * @param props.total - Total guideline count (for the `NN/NN` badge)
 * @param props.showHumanTag - Whether to render the human verdict tag
 * @param props.onSelect - Opens this guideline's detailed review; wired to the
 *   trailing edit (pencil) icon so only that icon reopens the detail screen
 * @returns The rendered row
 */
const MetRow: React.FC<{
  entry: GuidelineEntry;
  position: number;
  total: number;
  showHumanTag: boolean;
  onSelect?: (index: number) => void;
  onCopy?: (guideline: NCCNEvaluationRecord, displayIndex: number) => void;
}> = ({ entry, position, total, showHumanTag, onSelect, onCopy }) => {
  const { guideline, index } = entry;
  const aiTag = aiVerdictTag(guideline);
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <li className="flex w-full flex-col overflow-hidden rounded-md border border-[#E6E6E6] bg-white">
      <div className="flex w-full items-center gap-3 px-4 py-3">
        <Tags
          label={`${pad(position)}/${pad(total)}`}
          backgroundColor="#F5F5F5"
          textColor="#374151"
        />
        {!isExpanded && (
          <span className="max-w-[60%] truncate text-[13px] leading-5 text-[#0F0F0F]">
            {guideline.guideline}
          </span>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-3">
          <Tags
            label={aiTag.label}
            backgroundColor={aiTag.background}
            textColor={aiTag.text}
          />
          {showHumanTag && (
            <HumanVerdictTag
              verdict={guideline.metadata?.human_review?.verdict}
            />
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onSelect?.(index)}
              aria-label="Edit guideline review"
              className="flex cursor-pointer items-center border-0 bg-transparent p-0"
            >
              <EditPencilIcon
                width={14}
                height={14}
                stroke="#0041A3"
                strokeWidth={1}
              />
            </button>
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-expanded={isExpanded}
              aria-label={
                isExpanded ? "Collapse guideline" : "Expand guideline"
              }
              className={`flex cursor-pointer items-center border-0 bg-transparent p-0 transition-transform ${
                isExpanded ? "rotate-180" : ""
              }`}
            >
              <ChevronDown stroke="#6B6B6B" />
            </button>
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="flex items-end gap-3 self-stretch border-t border-[#E6E6E6] px-4 pb-2.5 pt-3">
          {/* Trim trailing newlines so pre-wrap doesn't render blank lines
              under the text. */}
          <p className="m-0 min-w-0 flex-1 whitespace-pre-wrap text-[0.75rem] font-normal leading-[1.1875rem] text-[#0F0F0F]">
            {guideline.guideline?.trimEnd()}
          </p>
          {isBothVerdictsMet(guideline) && (
            <div className="flex shrink-0 justify-end">
              <GuidelineCopyButton
                text={guideline.guideline ?? ""}
                label={`Copy guideline ${pad(position)} text`}
                onCopy={() => onCopy?.(guideline, position)}
              />
            </div>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * Card for a guideline whose effective verdict is "Not Met". The AI verdict tag
 * reflects the guideline's own AI result (so a reviewer-failed
 * AI-Met/AI-Undetermined guideline still reads "AI: Met" / "AI: Undetermined"),
 * and a reviewer's "Human: …" verdict tag is shown whenever one is recorded.
 * While the guideline is not yet reviewed, the card exposes the "Override to
 * Met" action; once reviewed, it exposes a pencil affordance that reopens the
 * review instead. The guideline text is collapsed to four lines by default and
 * expands to its full length when the trailing chevron is clicked.
 *
 * @param props.entry - Guideline plus its original index
 * @param props.position - 1-based review-order position (for the `NN/NN` badge)
 * @param props.total - Total guideline count (for the `NN/NN` badge)
 * @param props.onOverride - Opens this guideline's review
 * @returns The rendered card
 */
const NotMetCard: React.FC<{
  entry: GuidelineEntry;
  position: number;
  total: number;
  onOverrideToMet?: (index: number) => void;
  onEditReview?: (index: number) => void;
}> = ({ entry, position, total, onOverrideToMet, onEditReview }) => {
  const { guideline, index } = entry;
  const aiTag = aiVerdictTag(guideline);
  const humanVerdict = guideline.metadata?.human_review?.verdict;
  const isReviewed = !!humanVerdict;
  const [isExpanded, setIsExpanded] = useState(false);
  const containerClass =
    "flex w-full flex-col overflow-hidden rounded-md border border-[#E6E6E6] bg-white";
  const headerClass =
    "flex items-center gap-2.5 self-stretch border-b border-[#E6E6E6] bg-white px-3.5 py-2.5";

  return (
    <li className={containerClass}>
      <div className={headerClass}>
        <Tags
          label={`${pad(position)}/${pad(total)}`}
          backgroundColor="#F5F5F5"
          textColor="#374151"
        />
        <div
          className={`flex items-center gap-2.5 ${isReviewed ? "ml-auto" : ""}`}
        >
          <Tags
            label={aiTag.label}
            backgroundColor={aiTag.background}
            textColor={aiTag.text}
          />
          {isReviewed && <HumanVerdictTag verdict={humanVerdict} />}
        </div>
        {!isReviewed && (
          <button
            type="button"
            onClick={() => onOverrideToMet?.(index)}
            className="ml-auto flex h-[1.75rem] cursor-pointer items-center gap-[0.3125rem] rounded-[0.25rem] border border-[#0056D6] bg-white px-[0.75rem] py-0 text-center text-[0.6875rem] font-bold leading-[1.03125rem] text-[#0056D6] [font-family:Lato]"
          >
            <PlusIcon />
            Override to Met
          </button>
        )}
        {isReviewed && (
          <button
            type="button"
            onClick={() => onEditReview?.(index)}
            aria-label="Edit guideline review"
            className="flex cursor-pointer items-center border-0 bg-transparent p-0"
          >
            <EditPencilIcon
              width={14}
              height={14}
              stroke="#0041A3"
              strokeWidth={1}
            />
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Collapse guideline" : "Expand guideline"}
          className={`flex cursor-pointer items-center border-0 bg-transparent p-0 transition-transform ${
            isReviewed ? "" : "ml-1"
          } ${isExpanded ? "rotate-180" : ""}`}
        >
          <ChevronDown stroke="#6B6B6B" />
        </button>
      </div>

      <div className="flex flex-col items-start gap-3 self-stretch px-3.5 pb-2.5 pt-3">
        <p
          className={`m-0 self-stretch whitespace-pre-wrap text-[0.75rem] font-normal leading-[1.1875rem] text-[#0F0F0F] ${
            isExpanded ? "" : "line-clamp-5"
          }`}
        >
          {guideline.guideline}
        </p>
      </div>
    </li>
  );
};

/**
 * Title-and-counts header for the guideline summary ("Review Summary" plus the
 * total/met/not-met tags). Rendered on its own so it can sit in a fixed panel
 * bar, decoupled from the scrollable list body.
 *
 * @param props.guidelines - All guidelines in the evaluation
 * @returns The rendered header row content
 */
export const GuidelineSummaryHeader: React.FC<{
  guidelines: NCCNEvaluationRecord[];
}> = ({ guidelines }) => {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-bold text-[#0F0F0F]">NCCN Guidelines</span>
    </div>
  );
};

/**
 * Renders the grouped, AI-verdict-based summary of an evaluation.
 *
 * @param props - Guidelines and the override handler
 * @returns The rendered summary list
 */
export const GuidelineSummaryList: React.FC<GuidelineSummaryListProps> = ({
  guidelines,
  onOverrideToMet,
  onEditReview,
  onCopy,
  showHeader = true,
}) => {
  // Group by the effective verdict — the reviewer's human verdict when present,
  // otherwise the AI verdict — so an AI-Undetermined (or overridden) guideline
  // lands under Met or Not Met based on what the reviewer decided. The
  // effectively-Met group is further split so guidelines the reviewer actually
  // marked Met are shown separately from AI-Met ones that were never reviewed.
  const { humanReviewedMet, otherAiMet, notMet } = useMemo(() => {
    const entries: GuidelineEntry[] = guidelines.map((guideline, index) => ({
      guideline,
      index,
    }));
    return {
      humanReviewedMet: entries.filter(
        (entry) =>
          isEffectiveMet(entry.guideline) && isHumanMet(entry.guideline),
      ),
      otherAiMet: entries.filter(
        (entry) =>
          isEffectiveMet(entry.guideline) && !isHumanMet(entry.guideline),
      ),
      notMet: entries.filter((entry) => !isEffectiveMet(entry.guideline)),
    };
  }, [guidelines]);

  // Shared position map so each row's badge matches the number shown on the
  // detailed guideline header when that guideline is opened.
  const positions = useMemo(
    () => getGuidelineDisplayPositions(guidelines),
    [guidelines],
  );

  const total = guidelines.length;

  return (
    <div className="flex flex-col gap-[1.25rem]">
      {showHeader && (
        <div className="border-b border-gray-200 pb-3">
          <GuidelineSummaryHeader guidelines={guidelines} />
        </div>
      )}

      {humanReviewedMet.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: MET_COLORS.text }}
            />
            <span
              className="text-[11px] font-bold uppercase tracking-[0.6px]"
              style={{ color: MET_COLORS.text }}
            >
              Human Reviewed Met Guidelines
            </span>
          </div>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {humanReviewedMet.map((entry) => (
              <MetRow
                key={entry.guideline.guideline_id ?? entry.index}
                entry={entry}
                position={positions[entry.index] ?? entry.index + 1}
                total={total}
                showHumanTag
                onSelect={onEditReview}
                onCopy={onCopy}
              />
            ))}
          </ul>
        </section>
      )}

      {otherAiMet.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: UNDETERMINED_COLORS.text }}
            />
            <span
              className="text-[11px] font-bold uppercase tracking-[0.6px]"
              style={{ color: UNDETERMINED_COLORS.text }}
            >
              Other AI Met Guidelines
            </span>
          </div>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {otherAiMet.map((entry) => (
              <MetRow
                key={entry.guideline.guideline_id ?? entry.index}
                entry={entry}
                position={positions[entry.index] ?? entry.index + 1}
                total={total}
                showHumanTag={false}
                onSelect={onEditReview}
                onCopy={onCopy}
              />
            ))}
          </ul>
        </section>
      )}

      {notMet.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: NOT_MET_COLORS.text }}
            />
            <span
              className="text-[0.6875rem] font-bold uppercase leading-[1.03125rem] tracking-[0.03125rem]"
              style={{ color: NOT_MET_COLORS.text }}
            >
              Not Met Guidelines
            </span>
          </div>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {notMet.map((entry) => (
              <NotMetCard
                key={entry.guideline.guideline_id ?? entry.index}
                entry={entry}
                position={positions[entry.index] ?? entry.index + 1}
                total={total}
                onOverrideToMet={onOverrideToMet}
                onEditReview={onEditReview}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};
